import React, { useState, useEffect, useRef } from 'react';
import { Clock, FileText, AlertCircle, RotateCw } from 'lucide-react';
import { problemStats } from '../data/mock';

const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const Problem = () => {
  return (
    <section id="problem" className="problem-section">
      <div className="section-container">
        <h2 className="section-title fade-in">Das Problem</h2>
        
        <div className="problem-content">
          <div className="problem-intro">
            <p className="problem-highlight">
              Jedes Hypotheken-Dossier kostet bis zu <strong className="stat-highlight">45 Minuten</strong> manuelle Arbeit.
            </p>
          </div>

          <div className="problem-stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Clock size={32} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <AnimatedCounter end={problemStats.manualTime} suffix=" Min" />
                </div>
                <div className="stat-label">Pro Dossier (Manuell)</div>
              </div>
            </div>

            <div className="stat-card stat-card-success">
              <div className="stat-icon">
                <FileText size={32} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <AnimatedCounter end={problemStats.automatedTime} suffix=" Min" />
                </div>
                <div className="stat-label">Mit Doctimize</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <RotateCw size={32} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <AnimatedCounter end={problemStats.dossiersPerMonth} suffix="/Monat" />
                </div>
                <div className="stat-label">Durchschnittliche Dossiers</div>
              </div>
            </div>

            <div className="stat-card stat-card-warning">
              <div className="stat-icon">
                <AlertCircle size={32} />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <AnimatedCounter end={problemStats.wastedHours} suffix=" Std" />
                </div>
                <div className="stat-label">Verschwendete Zeit/Monat</div>
              </div>
            </div>
          </div>

          <div className="problem-list">
            <h3 className="problem-list-title">Typische Zeitfresser:</h3>
            <ul className="problem-items">
              <li className="problem-item">Lohnabrechnungen öffnen</li>
              <li className="problem-item">Zahlen übertragen</li>
              <li className="problem-item">Formatierungen anpassen</li>
              <li className="problem-item">Fehler korrigieren</li>
              <li className="problem-item">Prozesse wiederholen</li>
            </ul>
          </div>

          <div className="problem-conclusion">
            <p>
              Bei <strong>{problemStats.dossiersPerMonth} Dossiers pro Monat</strong> entstehen{' '}
              <strong className="stat-highlight">{problemStats.wastedHours} Stunden reine Copy-Paste-Arbeit</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;
